<template>
  <div
    class="dropdown-component"
    v-if="textArea || !!$slots.default"
    v-click-outside="closeDropdown"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <button
      type="button"
      class="dropdown-trigger"
      :class="[
        buttonClass,
        { 'dropdown-trigger--compact-icon': compact && iconOnly },
      ]"
      :aria-label="triggerLabel"
      :aria-expanded="String(isOpen)"
      :aria-controls="panelId"
      @click="toggleDropdown"
      v-haptic
      @mouseover="onMouseEnter"
    >
      <span v-if="!iconOnly">{{ title }}</span>
      <svg :class="compact ? 'tinyImg' : 'compactImg'" aria-hidden="true">
        <use :href="arrowIconHref"></use>
      </svg>
    </button>
    <transition name="slide-fade">
      <div
        :id="panelId"
        v-show="isOpen"
        class="text-area"
        :class="[openFrom, panelClass, { compact }]"
        :style="panelStyle"
        @mouseenter="onMouseEnter"
        @mouseover="onMouseEnter"
      >
        <slot :close="closeDropdown">{{ textArea }}</slot>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount } from "vue";
import iconsetPath from "@/assets/icons/iconset.svg";

let dropdownId = 0;

export default {
  name: "Dropdown",
  emits: ["open", "close"],
  props: {
    title: String,
    textArea: String,
    iconOnly: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    buttonClass: {
      type: String,
      default: "",
    },
    panelClass: {
      type: String,
      default: "",
    },
    zIndex: {
      type: [Number, String],
      default: null,
    },
    openFrom: {
      type: String,
      default: "leftdown",
      validator: (value) =>
        ["leftdown", "rightdown", "leftup", "rightup"].includes(value),
    },
    hoverTrigger: {
      type: Boolean,
      default: false,
    },
    triggerLabel: {
      type: String,
      default: "Open actions",
    },
  },
  setup(props, { emit }) {
    const isOpen = ref(false);
    const panelId = `dropdown-panel-${++dropdownId}`;
    let hoverTimeout = null;
    const arrowIconHref = computed(() =>
      `${iconsetPath}#${
        props.compact
          ? "arrowdown_carousel-tiny"
          : "arrowdown_carousel-compact"
      }`
    );
    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) emit("open");
      else emit("close");
    };
    const closeDropdown = () => {
      if (isOpen.value) {
        isOpen.value = false;
        emit("close");
      }
    };

    const supportsHover = () => {
      try {
        return (
          typeof window !== "undefined" &&
          window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches
        );
      } catch (_) {
        return false;
      }
    };

    const onMouseEnter = () => {
      if (!props.hoverTrigger || !supportsHover()) return;
      clearTimeout(hoverTimeout);
      hoverTimeout = window.setTimeout(() => {
        if (!isOpen.value) {
          isOpen.value = true;
          emit("open");
        }
      }, 150);
    };

    const onMouseLeave = () => {
      if (!props.hoverTrigger || !supportsHover()) return;
      clearTimeout(hoverTimeout);
      hoverTimeout = window.setTimeout(() => {
        if (isOpen.value) {
          isOpen.value = false;
          emit("close");
        }
      }, 200);
    };

    onBeforeUnmount(() => {
      clearTimeout(hoverTimeout);
    });

    const panelStyle = computed(() => {
      if (
        props.zIndex === null ||
        props.zIndex === undefined ||
        props.zIndex === ""
      )
        return {};
      return { zIndex: props.zIndex };
    });

    // Return the variables and methods you want to use in the template
    return {
      isOpen,
      toggleDropdown,
      closeDropdown,
      arrowIconHref,
      panelId,
      panelStyle,
      onMouseEnter,
      onMouseLeave,
    };
  },
};
</script>

<style scoped>
.dropdown-component {
  position: relative;
  display: inline-flex;
  align-items: center;
  overflow: visible;
}

.dropdown-trigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-width: 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  line-height: 1;
  cursor: pointer;
}

.dropdown-trigger--compact-icon {
  width: var(--control-height-dense);
  height: calc(var(--control-height-compact) * 0.4);
}

.dropdown-trigger--compact-icon::before {
  content: "";
  position: absolute;
  inset-block: calc(var(--space-5) * -1);
  inset-inline: calc(var(--space-1) * -1);
}

.dropdown-trigger:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.text-area {
  z-index: 2;
  padding: var(--space-5);
  position: absolute;
  max-height: 50vh;
  width: 40vw;
  max-width: 60vw;
  overflow-y: auto;
  border-radius: var(--radius-sm);
  background-color: var(--color-surface-canvas);
  box-shadow: 0 2px 6px var(--color-shadow-default);
}

.text-area.compact {
  padding: var(--space-2);
  width: max-content;
  max-width: 320px;
}

.leftdown {
  margin-top: calc(var(--control-height-default) * -0.85) !important;
  left: 0;
  bottom: 0;
}

.rightdown {
  margin-top: calc(var(--control-height-default) * -0.85) !important;
  right: 0;
  bottom: 0;
}

.leftup {
  margin-top: calc(var(--control-height-default) * 0.85) !important;
  left: 0;
  top: 0;
}

.rightup {
  margin-top: calc(var(--control-height-default) * 0.85) !important;

  right: 0;
  top: 0;
}
</style>
